import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star, Loader2, MessageSquare, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { vendorApi } from '@/services/api';
import { getVendorIdFromToken } from '@/services/auth';
import { Button, Card, CardContent } from '@nepal-ecom/ui';

interface ApiReview {
  id: string;
  rating: number;
  title?: string | null;
  comment?: string | null;
  reviewerName?: string | null;
  isVerifiedPurchase?: boolean;
  vendorReply?: string | null;
  vendorRepliedAt?: string | null;
  productId?: string;
  createdAt: string;
}

export function Reviews() {
  const [reviews, setReviews] = useState<ApiReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);

  const load = async () => {
    const vendorId = getVendorIdFromToken();
    if (!vendorId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await vendorApi.getReviews(vendorId, { limit: 50 });
      const payload = res.data?.data;
      setReviews(Array.isArray(payload) ? payload : payload?.data ?? []);
    } catch (e) {
      toast.error('Could not load reviews', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const submitReply = async (reviewId: string) => {
    const reply = replyDraft[reviewId]?.trim();
    if (!reply) return toast.error('Reply text required');
    setSubmitting(reviewId);
    try {
      await vendorApi.replyToReview(reviewId, reply);
      toast.success('Reply published');
      setReplyDraft((d) => ({ ...d, [reviewId]: '' }));
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        ?? (e as Error).message;
      toast.error('Reply failed', { description: msg });
    } finally {
      setSubmitting(null);
    }
  };

  const avg = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : '—';

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Customer reviews"
        description={loading ? 'Loading…' : `${reviews.length} approved review${reviews.length === 1 ? '' : 's'} · ${avg}★ average`}
      />

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Star className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="font-semibold text-slate-900">No reviews yet</p>
            <p className="text-sm text-slate-500 mt-1">Customers can leave reviews after their order is delivered.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center flex-shrink-0">
                      {(r.reviewerName ?? 'A').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
                        {r.reviewerName ?? 'Anonymous'}
                        {r.isVerifiedPurchase && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" aria-label="Verified buyer" />}
                      </p>
                      <p className="text-xs text-slate-500">{new Date(r.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-4 w-4 ${s <= r.rating ? 'fill-amber-400 text-amber-400' : 'fill-slate-200 text-slate-200'}`} />
                    ))}
                  </div>
                </div>
                {r.title && <p className="text-sm font-semibold text-slate-900">{r.title}</p>}
                {r.comment && <p className="text-sm text-slate-600 leading-relaxed">{r.comment}</p>}

                {r.vendorReply ? (
                  <div className="rounded-lg bg-slate-50 border-l-4 border-emerald-600 p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1 inline-flex items-center gap-1.5">
                      <MessageSquare className="h-3 w-3" /> Your reply
                    </p>
                    <p className="text-sm text-slate-700">{r.vendorReply}</p>
                    {r.vendorRepliedAt && (
                      <p className="text-xs text-slate-400 mt-1">{new Date(r.vendorRepliedAt).toLocaleDateString()}</p>
                    )}
                  </div>
                ) : (
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <textarea
                      value={replyDraft[r.id] ?? ''}
                      onChange={(e) => setReplyDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                      placeholder="Thanks for your review…"
                      rows={2}
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                    <Button size="sm" onClick={() => submitReply(r.id)} disabled={submitting === r.id}>
                      {submitting === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Reply'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
